/**
 * Bootstrap del local desde la URL.
 *
 * pathname → slug → rpc_resolver_local_por_slug → { id, slug, nombre, activo }
 */

import { useEffect, useMemo, useState } from "react";
import { resolveSlugFromUrl } from "../utils/slugUrl";
import { resolverLocalPorSlug } from "../services/localesService";

/**
 * @typedef {'sin_slug'|'cargando'|'ok'|'no_encontrado'|'inactivo'|'error'} EstadoLocalPortal
 */

export function useLocalPortal() {
  const slug = useMemo(() => resolveSlugFromUrl(), []);
  const [local, setLocal] = useState(null);
  const [estado, setEstado] = useState(
    /** @type {EstadoLocalPortal} */ (slug ? "cargando" : "sin_slug")
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setLocal(null);
      setEstado("sin_slug");
      return;
    }

    let cancelado = false;

    async function cargar() {
      setEstado("cargando");
      setError(null);

      try {
        const row = await resolverLocalPorSlug(slug);
        if (cancelado) return;

        if (!row) {
          setLocal(null);
          setEstado("no_encontrado");
          return;
        }

        setLocal(row);
        setEstado(row.activo ? "ok" : "inactivo");
      } catch (err) {
        if (cancelado) return;
        console.error("Error resolviendo local por slug:", err);
        setLocal(null);
        setError(err);
        setEstado("error");
      }
    }

    cargar();

    return () => {
      cancelado = true;
    };
  }, [slug]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const nombre = local?.nombre;
    document.title = nombre ? `${nombre} - Menuee` : "Menuee";

    const description = nombre
      ? `${nombre} - Menuee — Carta digital y pedidos online.`
      : "Menuee — Carta digital y pedidos online.";

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", description);
    }

    const appleTitle = document.querySelector(
      'meta[name="apple-mobile-web-app-title"]'
    );
    if (appleTitle) {
      appleTitle.setAttribute("content", nombre ? `${nombre} - Menuee` : "Menuee");
    }
  }, [local]);

  return { slug, local, estado, error };
}
